import { useState } from 'react';
import { clsx } from 'clsx';
import {
  Code,
  Terminal,
  Key,
  Zap,
  Shield,
  Clock,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';

type Language = 'curl' | 'python' | 'javascript';

interface Endpoint {
  method: 'GET' | 'POST' | 'DELETE';
  path: string;
  description: string;
  params?: { name: string; type: string; required: boolean; description: string }[];
  response: string;
}

interface EndpointSection {
  title: string;
  description: string;
  endpoints: Endpoint[];
}

const V1_ENDPOINTS: Endpoint[] = [
  {
    method: 'GET',
    path: '/api/v1/scan',
    description: 'Get market scan results with top opportunities and sell signals.',
    params: [
      { name: 'limit', type: 'integer', required: false, description: 'Max results per category (default: 20)' },
      { name: 'min_disconnect', type: 'number', required: false, description: 'Min disconnect score (default: 30)' },
    ],
    response: `{
  "timestamp": "2026-01-30T10:30:00Z",
  "stocks_analyzed": 28,
  "buying_opportunities": [
    {"ticker": "PYPL", "disconnect_score": 98.8, "signal": "STRONG_BUY", ...}
  ],
  "sell_signals": [
    {"ticker": "TSLA", "disconnect_score": 20.8, "signal": "STRONG_SELL", ...}
  ]
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/analyze/{ticker}',
    description: 'Get full disconnect analysis for a stock including sentiment, fundamentals, and technical scores.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol (e.g., AAPL)' },
      { name: 'fresh', type: 'boolean', required: false, description: 'Force fresh analysis (default: false, uses cache)' },
    ],
    response: `{
  "ticker": "AAPL",
  "timestamp": "2026-01-30T10:30:00Z",
  "cached": true,
  "scores": {
    "disconnect": 72.5,
    "sentiment": 65.2,
    "fundamental": 85.0,
    "technical": 56.7
  },
  "signal": {
    "type": "BUY",
    "strength": 68.5
  },
  "price": 258.28,
  "explanation": "AAPL shows a positive disconnect..."
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/sentiment/{ticker}',
    description: 'Get real-time sentiment analysis from X/Twitter including themes, notable posts, and breakdown.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
    ],
    response: `{
  "ticker": "AAPL",
  "sentiment": {
    "score": 65,
    "label": "bullish",
    "confidence": 0.85
  },
  "breakdown": {
    "bullish_pct": 62,
    "bearish_pct": 18,
    "neutral_pct": 20
  },
  "themes": ["AI integration", "earnings beat", "buybacks"],
  "notable_posts": [...],
  "summary": "Overall bullish sentiment..."
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/history/{ticker}',
    description: 'Get historical sentiment, disconnect, and price data for charting.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'days', type: 'integer', required: false, description: 'Days of history (default: 30, max: 1095)' },
    ],
    response: `{
  "ticker": "AAPL",
  "days": 30,
  "data": {
    "sentiment": [{"date": "...", "score": 65, "label": "bullish", ...}],
    "disconnect": [{"date": "...", "score": 72.5, "signal": "BUY"}],
    "prices": [{"date": "...", "open": 255, "close": 258, ...}]
  }
}`,
  },
];

const DATA_ENDPOINTS: Endpoint[] = [
  {
    method: 'GET',
    path: '/api/data/quote/{ticker}',
    description: 'Get real-time stock quote with price, volume, and change.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
    ],
    response: `{
  "ticker": "AAPL",
  "price": 258.28,
  "open": 255.50,
  "high": 259.10,
  "low": 254.80,
  "volume": 45230000,
  "previous_close": 256.12,
  "change": 2.16,
  "change_percent": 0.84,
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/profile/{ticker}',
    description: 'Get company profile including sector, industry, description, and market cap.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
    ],
    response: `{
  "ticker": "AAPL",
  "name": "Apple Inc",
  "sector": "Technology",
  "industry": "Consumer Electronics",
  "description": "Apple Inc. designs, manufactures...",
  "market_cap": 3890000000000,
  "exchange": "NASDAQ",
  "currency": "USD",
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/metrics/{ticker}',
    description: 'Get key financial metrics and ratios (P/E, ROE, margins, dividends).',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
    ],
    response: `{
  "ticker": "AAPL",
  "valuation": {
    "pe_ratio": 32.5,
    "forward_pe": 28.2,
    "peg_ratio": 2.1,
    "price_to_book": 48.5
  },
  "profitability": {
    "profit_margin": 0.26,
    "roe": 1.47,
    "roa": 0.28
  },
  "dividends": {
    "dividend_yield": 0.0044,
    "payout_ratio": 0.15
  },
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/historical/{ticker}',
    description: 'Get historical OHLCV price data for the specified period.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'period', type: 'string', required: false, description: 'Period: 1m, 3m, 6m, 1y, 2y, 5y, max (default: 3m)' },
    ],
    response: `{
  "ticker": "AAPL",
  "period": "3m",
  "count": 90,
  "prices": [
    {"date": "2026-01-30", "open": 255.5, "high": 259.1, "low": 254.8, "close": 258.28, "volume": 45230000}
  ],
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/earnings/{ticker}',
    description: 'Get historical earnings with EPS estimates and surprise percentages.',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
    ],
    response: `{
  "ticker": "AAPL",
  "annual": [
    {"fiscal_date": "2025-09-30", "reported_eps": 6.42, "estimated_eps": 6.35, "surprise_pct": 1.1}
  ],
  "quarterly": [
    {"fiscal_date": "2025-12-31", "reported_eps": 2.18, "estimated_eps": 2.10, "surprise_pct": 3.8}
  ],
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/income-statement/{ticker}',
    description: 'Get historical income statement data (revenue, profit, expenses).',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'period', type: 'string', required: false, description: 'Period: annual or quarterly (default: quarterly)' },
    ],
    response: `{
  "ticker": "AAPL",
  "period": "quarterly",
  "reports": [
    {"fiscal_date": "2025-12-31", "total_revenue": 124300000000, "gross_profit": 58200000000, "net_income": 33900000000}
  ],
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/cash-flow/{ticker}',
    description: 'Get historical cash flow data (operating cash, CapEx, FCF, buybacks).',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'period', type: 'string', required: false, description: 'Period: annual or quarterly (default: quarterly)' },
    ],
    response: `{
  "ticker": "AAPL",
  "period": "quarterly",
  "reports": [
    {"fiscal_date": "2025-12-31", "operating_cashflow": 39800000000, "capital_expenditures": -2800000000, "free_cash_flow": 37000000000}
  ],
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/balance-sheet/{ticker}',
    description: 'Get historical balance sheet data (assets, liabilities, equity).',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'period', type: 'string', required: false, description: 'Period: annual or quarterly (default: quarterly)' },
    ],
    response: `{
  "ticker": "AAPL",
  "period": "quarterly",
  "reports": [
    {"fiscal_date": "2025-12-31", "total_assets": 352800000000, "total_liabilities": 290500000000, "total_shareholder_equity": 62300000000}
  ],
  "source": "SentimentEdge"
}`,
  },
  {
    method: 'GET',
    path: '/api/data/financials/{ticker}',
    description: 'Get all financial statements combined (income, cash flow, balance sheet).',
    params: [
      { name: 'ticker', type: 'string', required: true, description: 'Stock ticker symbol' },
      { name: 'period', type: 'string', required: false, description: 'Period: annual or quarterly (default: quarterly)' },
    ],
    response: `{
  "ticker": "AAPL",
  "period": "quarterly",
  "income_statement": [...],
  "cash_flow": [...],
  "balance_sheet": [...],
  "source": "SentimentEdge"
}`,
  },
];

const ENDPOINT_SECTIONS: EndpointSection[] = [
  {
    title: 'Sentiment Analysis API (V1)',
    description: 'Access disconnect analysis, sentiment data, and market signals. Requires Enterprise API key.',
    endpoints: V1_ENDPOINTS,
  },
  {
    title: 'Market Data API',
    description: 'Access raw financial data including quotes, company profiles, and financial statements. Requires user authentication.',
    endpoints: DATA_ENDPOINTS,
  },
];


const getCodeExample = (endpoint: Endpoint, language: Language, apiKey: string = 'sk_your_api_key'): string => {
  const baseUrl = 'https://api.stocksentiment.io';
  const path = endpoint.path.replace('{ticker}', 'AAPL');

  switch (language) {
    case 'curl':
      return `curl -X ${endpoint.method} "${baseUrl}${path}" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json"`;

    case 'python':
      return `import requests

response = requests.${endpoint.method.toLowerCase()}(
    "${baseUrl}${path}",
    headers={
        "X-API-Key": "${apiKey}",
        "Content-Type": "application/json"
    }
)

data = response.json()
print(data)`;

    case 'javascript':
      return `const response = await fetch("${baseUrl}${path}", {
  method: "${endpoint.method}",
  headers: {
    "X-API-Key": "${apiKey}",
    "Content-Type": "application/json"
  }
});

const data = await response.json();
console.log(data);`;
  }
};

function CodeBlock({ code, language: _language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="p-4 rounded-lg bg-[#1a1a2e] text-gray-300 text-sm overflow-x-auto font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className={clsx(
          'absolute top-2 right-2 p-2 rounded-lg transition-all',
          'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]',
          'hover:text-[var(--color-text-primary)]',
          'opacity-0 group-hover:opacity-100'
        )}
      >
        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function EndpointCard({ endpoint }: { endpoint: Endpoint }) {
  const [language, setLanguage] = useState<Language>('curl');
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-[var(--color-border)] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-4 hover:bg-[var(--color-bg-secondary)] transition-colors"
      >
        <span
          className={clsx(
            'px-3 py-1 text-xs font-bold rounded',
            endpoint.method === 'GET' && 'bg-blue-500/20 text-blue-400',
            endpoint.method === 'POST' && 'bg-emerald-500/20 text-emerald-400',
            endpoint.method === 'DELETE' && 'bg-red-500/20 text-red-400'
          )}
        >
          {endpoint.method}
        </span>
        <code className="text-sm font-mono text-[var(--color-text-primary)]">{endpoint.path}</code>
        <span className="flex-1 text-left text-sm text-[var(--color-text-muted)] truncate">
          {endpoint.description}
        </span>
        <ChevronRight
          className={clsx(
            'h-5 w-5 text-[var(--color-text-muted)] transition-transform',
            expanded && 'rotate-90'
          )}
        />
      </button>

      {expanded && (
        <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
          <p className="text-[var(--color-text-secondary)] mb-4">{endpoint.description}</p>

          {endpoint.params && endpoint.params.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">
                Parameters
              </h4>
              <div className="space-y-2">
                {endpoint.params.map((param) => (
                  <div key={param.name} className="flex items-start gap-2 text-sm">
                    <code className="px-2 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-accent)]">
                      {param.name}
                    </code>
                    <span className="text-[var(--color-text-muted)]">{param.type}</span>
                    {param.required && (
                      <span className="text-xs text-red-400">required</span>
                    )}
                    <span className="text-[var(--color-text-secondary)]">
                      - {param.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              Example Request
            </h4>
            <div className="flex gap-2 mb-2">
              {(['curl', 'python', 'javascript'] as Language[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded transition-colors',
                    language === lang
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                  )}
                >
                  {lang === 'curl' ? 'cURL' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                </button>
              ))}
            </div>
            <CodeBlock code={getCodeExample(endpoint, language)} language={language} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              Example Response
            </h4>
            <CodeBlock code={endpoint.response} language="json" />
          </div>
        </div>
      )}
    </div>
  );
}

export function APIDocs() {
  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">API Documentation</h1>
        <p className="text-[var(--color-text-muted)] mt-2">
          Build powerful integrations with SentimentEdge's disconnect analysis API
        </p>
      </div>

      {/* Quick Start */}
      <Card className="border-l-4 border-l-[var(--color-accent)]">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-[var(--color-accent)]/10">
            <Zap className="h-6 w-6 text-[var(--color-accent)]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Quick Start</h3>
            <p className="text-[var(--color-text-muted)] mt-1">
              The SentimentEdge API provides programmatic access to our disconnect analysis engine.
              Enterprise users can integrate real-time sentiment analysis into their own applications.
            </p>
            <div className="mt-4 flex gap-3">
              <Button variant="primary" onClick={() => window.location.href = '/app/settings#api-keys'}>
                Get API Key
              </Button>
              <Button variant="secondary" onClick={() => window.open('/docs', '_blank')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                OpenAPI Docs
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader
          title="Authentication"
          subtitle="All API requests require an API key"
          icon={<Key className="h-5 w-5" />}
        />

        <div className="space-y-4">
          <p className="text-[var(--color-text-secondary)]">
            Include your API key in the <code className="px-2 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-accent)]">X-API-Key</code> header with every request.
          </p>

          <CodeBlock
            code={`curl -X GET "https://api.stocksentiment.io/api/v1/analyze/AAPL" \\
  -H "X-API-Key: sk_your_api_key"`}
            language="bash"
          />

          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-500">Security</p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Keep your API key secret. Never expose it in client-side code or public repositories.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Rate Limits */}
      <Card>
        <CardHeader
          title="Rate Limits"
          subtitle="Fair usage ensures reliability for everyone"
          icon={<Clock className="h-5 w-5" />}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">1,000</p>
            <p className="text-sm text-[var(--color-text-muted)]">requests per minute</p>
          </div>
          <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">1,095</p>
            <p className="text-sm text-[var(--color-text-muted)]">days of historical data</p>
          </div>
        </div>

        <p className="text-sm text-[var(--color-text-muted)] mt-4">
          Rate limit headers are included in every response:
        </p>
        <CodeBlock
          code={`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 998
X-RateLimit-Reset: 1706182800`}
          language="http"
        />
      </Card>

      {/* Endpoints by Section */}
      {ENDPOINT_SECTIONS.map((section, sectionIndex) => (
        <div key={sectionIndex}>
          <div className="flex items-center gap-3 mb-2">
            <Terminal className="h-6 w-6 text-[var(--color-accent)]" />
            <h2 className="text-xl font-bold text-[var(--color-text-primary)]">{section.title}</h2>
          </div>
          <p className="text-[var(--color-text-muted)] mb-4">{section.description}</p>

          <div className="space-y-4">
            {section.endpoints.map((endpoint, i) => (
              <EndpointCard key={i} endpoint={endpoint} />
            ))}
          </div>
        </div>
      ))}

      {/* SDKs */}
      <Card>
        <CardHeader
          title="SDKs & Libraries"
          subtitle="Coming soon"
          icon={<Code className="h-5 w-5" />}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'Python', status: 'Coming Soon' },
            { name: 'JavaScript/TypeScript', status: 'Coming Soon' },
            { name: 'Go', status: 'Planned' },
          ].map((sdk) => (
            <div
              key={sdk.name}
              className="p-4 rounded-lg bg-[var(--color-bg-secondary)] text-center"
            >
              <p className="font-medium text-[var(--color-text-primary)]">{sdk.name}</p>
              <p className="text-sm text-[var(--color-text-muted)]">{sdk.status}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Support */}
      <Card>
        <div className="text-center py-4">
          <p className="text-[var(--color-text-secondary)]">
            Need help? Contact our API support team at{' '}
            <a href="mailto:api@stocksentiment.io" className="text-[var(--color-accent)] hover:underline">
              api@stocksentiment.io
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
}
